import type { ExtendedInitiative } from '../../../../../api/initiative-client/initiative-client.type';
import type { Team } from '../../../../../api/projects-client/projects-client.type';
import type { Epic } from '../../../../../api/tasks-client/task-client.type';
import i18n from '../../../../../base-dictionary';
import { getContributingTeams } from '../../../initiative-list/main-section/main-section.helpers';
import { formatInitiativeDate } from '../../../strategy-v2.helpers';

/**
 * Retrieves formatted information about an epic for display
 *
 * @param {Epic} epic - The epic to get information for
 * @param {ExtendedInitiative | null} initiative - The initiative that the epic belongs to
 * @returns {Array<{label: string, value: string | number | null | undefined}>} An array of label-value pairs with epic information
 */
const getEpicInfo = (
  epic: Epic,
  initiative: ExtendedInitiative | null,
): { label: string; value: string | number | null | undefined }[] => {
  return [
    {
      label: i18n.t('common.type'),
      value: i18n.t('common.epic'),
    },
    {
      label: i18n.t('common.created'),
      value: formatInitiativeDate(epic.created_date),
    },
    {
      label: i18n.t('strategy.initiative_performance.cost_to_date'),
      value: initiative?.costs_to_date?.epics[epic.id] ?? i18n.t('strategy.initiatives.labels.no_data', '-'),
    },
    {
      label: i18n.t('common.teams'),
      value: initiative
        ? getContributingTeams(initiative, epic.projects as Team[])
        : i18n.t('strategy.initiatives.labels.no_data', '-'),
    },
  ];
};

/**
 * Calculates the completion percentage of an epic based on its subtasks
 *
 * @param {Epic} epic - The epic to calculate completion percentage for
 * @returns {number} The percentage of completed subtasks, rounded to the nearest integer (0-100)
 */
const getCompletionPercentage = (epic: Epic): number => {
  return epic.subtasks_completed_count && epic.subtasks_count
    ? Math.round((epic.subtasks_completed_count / epic.subtasks_count) * 100)
    : 0;
};

export { getCompletionPercentage, getEpicInfo };
